import {SSLAConfiguration} from "../config";
import * as Sco from "./sco";
import {Manifest} from "./manifest";
import {StorageEventType} from "./events";


declare let reqwest: any;
declare let pako: any;


interface SaveOptions {
    forceSync: boolean;
    eventType: StorageEventType;
    scoName: string;
}

export interface StorageInterface {
    load(options: any): void;

    notifyPlayerExit(): void;

    save(data: any, options: SaveOptions): void;
}

interface SaveData {
    action: string;
    currentSco: string;
    data: string;
    activityId: string;
    studentId: string;
    timestamp: number;
    scoOrdering?: string;
}

export class AllInOneStorage implements StorageInterface {
    public config: SSLAConfiguration;
    public cachedData: any = null;
    public allScos: Sco.AllScosData;
    public manifest: Manifest;
    public playerIsExiting = false;
    public isSaving: boolean;
    public waitingToSave?: SaveOptions;
    public worker: Worker;
    public storageIntervalHandle: number;

    constructor(config: SSLAConfiguration, allScos: Sco.AllScosData, manifest: Manifest) {
        this.config = config;
        this.allScos = allScos;
        this.manifest = manifest;

        if (this.config.storageSaveFrequency() > 0) {
            this.storageIntervalHandle = setInterval(this.storageIntervalHandler.bind(this), this.config.storageSaveFrequency());
        }
    }

    storageIntervalHandler() {
        if (this.allScos.isDirty()) {
            this.save(null, {
                forceSync: false,
                eventType: "commit",
                scoName: this.allScos.currentScoId,
            })
        }
    }

    storageSetValueTimeoutHandler() {
        this.saveDequeueIfAvailable();
    }

    load(options: any) {
        let me = this;
        if (!this.cachedData) {
            let success = false;
            reqwest({
                url: this.config.getDataUrl(),
                method: this.config.getDataAjaxMethod(),
                headers: this.config.getDataHeaders(),
                type: "json",
                async: true,
                crossOrigin: this.config.storageAllowCORS(),
                withCredentials: this.config.storageUseWithCredentials(),
                data: {
                    // TODO: Stop using global functions here.
                    "activityId": this.config.courseId(),
                    "studentId": this.config.studentId(),
                    "timestamp": new Date().getTime()
                },
                success: function (resp: any) {
                    if (typeof (resp) == "undefined" || !resp ||
                        "responseText" in resp ||
                        "responseXML" in resp) {
                        resp = {};
                    }
                    try {
                        if (me.config.storageCallbackAfterLoad()) {
                            let result = me.config.storageCallbackAfterLoad()({
                                success: true,
                                data: resp
                            });
                            if (typeof result != "undefined") {
                                resp = result;
                            }
                        }
                    } catch (e) {
                        console.log("Error while executing storage load callback", e);
                    }

                    me.cachedData = resp;
                    me.allScos.importData(me.cachedData);
                    options.callback(true);
                },
                error: function (request: any, status: any, error: any) {
                    console.log("error in get ajax" + request + "," + status + "," + error);
                    try {
                        if (me.config.storageCallbackAfterLoad()) {
                            me.config.storageCallbackAfterLoad()({
                                success: false,
                                ajaxRequest: request,
                                ajaxStatus: status,
                                ajaxError: error
                            });
                        }
                    } catch (e) {
                        console.log("Error while executing storage load callback", e);
                    }
                    options.callback(false);
                }
            });
        } else {
            // The data is already loaded, use the cache.
            me.allScos.importData(me.cachedData);
            options.callback(true);
        }
    }

    notifyPlayerExit() {
        this.playerIsExiting = true;
    }

    save(data: any, options: SaveOptions) {
        if (!options) {
            options = {
                forceSync: false,
                eventType: "save",
                scoName: ""
            };
        }
        if (!options.eventType) {
            options.eventType = "save";
        }
        if (!options.scoName) {
            options.scoName = "";
        }

        // Delay for long enough for the other set values to come in, we always debounce these.
        if (options.eventType == "setValue" && this.config.storageQueueSaves()) {
            this.waitingToSave = options;
            setTimeout(this.storageSetValueTimeoutHandler.bind(this), 500);
            return;
        }

        // Finish/terminate always gets called.  Everything else has a chance at a debounce if the configuration
        // option is enabled.
        if (options.eventType == "finish") {
            this.waitingToSave = null;
            this._save(options);
        } else {
            if (this.config.storageQueueSaves() && this.isSaving) {
                this.waitingToSave = options;
            } else {
                this._save(options);
            }
        }
    }

    getBodyAndHeaders(options: SaveOptions, saveData: SaveData) {
        let isRawBytes = false;
        let params = new URLSearchParams();
        let body = params.toString();
        Object.keys(saveData).forEach(function (k) {
            params.append(k, (saveData as any)[k].toString());
        });
        let headers = this.config.setDataHeaders();
        if (this.config.storageGzipRequestBody()) {
            isRawBytes = true;
            body = pako.gzip(params.toString(), {level: pako.Z_BEST_COMPRESSION});
            headers["Content-Encoding"] = "gzip";
        } else {
            // Don't override existing content-type header if one exists.
            // TODO Make this lookup case insensitive.  Or switch to using Headers (which will need polyfill).
            if (!headers["Content-Type"]) {
                headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
            }
        }
        return {isRawBytes: isRawBytes, body: body, params: params, headers: headers};
    }

    saveDequeueIfAvailable() {
        if (this.waitingToSave != null) {
            this._save(this.waitingToSave);
            this.waitingToSave = null;
        }
    }

    saveFetch(options: SaveOptions, saveData: SaveData) {
        let {isRawBytes, body, params, headers} = this.getBodyAndHeaders(options, saveData);
        let keepalive = false;
        if ((options.forceSync || this.playerIsExiting) && body.length < 60000) {
            keepalive = true;
        }
        const controller = new AbortController();
        const promise = fetch(
            this.config.setDataUrl(),
            {
                signal: controller.signal,
                method: this.config.setDataAjaxMethod(),
                headers: headers,
                mode: this.config.storageAllowCORS() ? "cors" : "same-origin",
                credentials: this.config.storageUseWithCredentials() ? "include" : "same-origin",
                keepalive: keepalive,
                body: isRawBytes ? body : params,
            }
        );
        controller.signal.addEventListener("abort", () => controller.abort());
        const timeout = setTimeout(() => controller.abort(), this.config.storageTimeout());
        let fetchResponse: any = null;
        let fetchError: any = null;
        let success: boolean = false;
        promise
            .then((response) => {
                fetchResponse = response;
                success = true;
            })
            .catch((error) => {
                fetchError = error;
                console.error("There was an error saving your data:", error);
            })
            .finally(() => {
                this.isSaving = false;
                clearTimeout(timeout);
                try {
                    if (this.config.storageCallbackAfterSave()) {
                        this.config.storageCallbackAfterSave()({
                            success: success,
                            fetchResponse: fetchResponse,
                            fetchError: fetchError
                        });
                    }
                } catch (e) {
                    console.log("Error while executing storage save callback (A)", e);
                }
                this.saveDequeueIfAvailable();
            });
    }

    saveXhr(options: SaveOptions, saveData: SaveData) {
        let me = this;
        let success = false;
        let ajaxResponse: any = null;
        let ajaxError: any = null;
        let async: boolean = true;
        // TODO: Restore this.config.forceSynchronousAjaxOnEnd
        if (options.forceSync || this.playerIsExiting) {
            async = false;
        }
        let {isRawBytes, body, params, headers} = this.getBodyAndHeaders(options, saveData);

        reqwest({
            url: this.config.setDataUrl(),
            method: this.config.setDataAjaxMethod(),
            headers: headers,
            async: async,
            crossOrigin: this.config.storageAllowCORS(),
            withCredentials: this.config.storageUseWithCredentials(),
            timeout: this.config.storageTimeout(),
            // We use saveData here because it works for all XHR cases, URLSearchParams acts weird on the polyfill.
            data: isRawBytes ? body : saveData,
            success: function (msg: any) {
                success = true;
                ajaxResponse = msg;
            },
            error: function (msg: any) {
                console.error("There was an error saving your data:", msg);
                ajaxError = msg;
            },
            complete: function (resp: any) {
                me.isSaving = false;
                try {
                    if (me.config.storageCallbackAfterSave()) {
                        me.config.storageCallbackAfterSave()({
                            success: success,
                            ajaxResponse: ajaxResponse,
                            ajaxError: ajaxError
                        });
                    }
                } catch (e) {
                    console.log("Error while executing storage save callback (B)", e);
                }
                me.saveDequeueIfAvailable();
            }
        });
    }

    _save(options: SaveOptions) {
        this.isSaving = true;
        let saveData: SaveData = {
            "action": options.eventType,
            "currentSco": options.scoName,
            "data": JSON.stringify(this.allScos.exportData(this.config, true)),
            "activityId": this.config.courseId(),
            "studentId": this.config.studentId(),
            "timestamp": new Date().getTime(),
        };
        // Mark the data as clean since we're about to save it so we don't re-buffer.
        this.allScos.clean();
        if (this.config.storageIncludeScoDetails()) {
            saveData["scoOrdering"] = JSON.stringify(this.manifest.scoPreOrderTraversal.map(sco => {
                return {"id": sco.identifier, "title": sco.title};
            }));
        }

        // Save if one of the following is true:
        // 1) action is not "commit" (multiple initializes and finishes should be saved if they can somehow happen)
        // 2) Duplicate commits are allowed
        // 3) Unique data is being saved
        if (options.eventType !== "commit" || this.config.storageSaveDuplicateCommits() || this.allScos.isDirty()) {
            if (this.config.storageUseFetch()) {
                this.saveFetch(options, saveData);
            } else {
                this.saveXhr(options, saveData);
            }
        }
    }
}
